import { snakeCaseToCamelCase } from '@elo-kit/utils/nameStyle.utils'
import { createId } from '@elo-kit/utils/general.utils'

import { PAYMENT_FORMS, PAYMENT_PROVIDERS } from 'constants/paymentSettingShared.constants'
import { LOCALES } from '@elo-kit/constants/locales.constants'
import { P24_PAYMENT_KEY } from 'constants/paymentSetting.constants'
import { getLocalisedMonduIcon, getLocalisedMonduLogo } from 'utils/mondu.utils'

import Card from 'shared/components/purchase-methods/assets/paymethods/Card.svg'
import AmEx from 'images/payment_methods/AmEx.svg'
import StripeCard from 'shared/components/purchase-methods/assets/paymethods/StripeCard.svg'
import PayPal from 'shared/components/purchase-methods/assets/paymethods/PayPal.svg'
import BankWire from 'shared/components/purchase-methods/assets/paymethods/BankWire.svg'
import BankWireDe from 'shared/components/purchase-methods/assets/paymethods/BankWireDe.svg'
import Sepa from 'shared/components/purchase-methods/assets/paymethods/Sepa.svg'
import Sofort from 'images/payment_methods/SofortDark.svg'
import PayLater from 'shared/components/purchase-methods/assets/paymethods/PayLater.svg'
import PayLaterDe from 'shared/components/purchase-methods/assets/paymethods/PayLaterDe.svg'
import P24 from 'shared/components/purchase-methods/assets/paymethods/P24.svg'
import ExtBalance from 'shared/components/purchase-methods/assets/paymethods/ExtBalance.svg'
import freeEn from 'shared/components/purchase-methods/assets/paymethods/free_en.svg'
import freeDe from 'shared/components/purchase-methods/assets/paymethods/free_de.svg'
import BankAccount from 'shared/components/purchase-methods/assets/paymethods/BankAccount.svg'
import BankAccountDe from 'shared/components/purchase-methods/assets/paymethods/BankAccountDe.svg'
import ApplePay from 'shared/components/purchase-methods/assets/paymethods/ApplePay.svg'
import GooglePay from 'shared/components/purchase-methods/assets/paymethods/GooglePay.svg'
import Ideal from 'shared/components/purchase-methods/assets/paymethods/Ideal.svg'
import IdealSepa from 'shared/components/purchase-methods/assets/paymethods/IdealSepa.svg'
import Twint from 'shared/components/purchase-methods/assets/paymethods/Twint.svg'
import Klarna from 'images/payment_methods/klarna.svg'
import PayByBank from 'shared/components/purchase-methods/assets/paymethods/PayByBank.svg'
import EloSveaPayLaterDe from 'shared/components/purchase-methods/assets/paymethods/SveaPayLaterDe.svg'
import EloSveaPayLaterEn from 'shared/components/purchase-methods/assets/paymethods/SveaPayLaterEn.svg'
import {
  EloAmExIcon,
  EloApplePayIcon,
  EloBankWireDeIcon,
  EloBankWireEnIcon,
  EloGooglePayIcon,
  EloIdealIcon,
  EloKlarnaIcon,
  EloMaestroIcon,
  EloMastercardIcon,
  EloPayByBankIcon,
  EloPayLaterDeIcon,
  EloPayLaterEnIcon,
  EloPaypalIcon,
  EloPrzelewy24Icon,
  EloSepaIcon,
  EloPayInInstallmentsEnIcon,
  EloPayInInstallmentsDeIcon,
  EloTwintIcon,
  EloVisaIcon,
} from '@elo-ui/components/icons/payment-methods'

import { DEFAULT_COUNTRY_CODE } from 'constants/countries.constants'
import { LogoVariant, PayByBankCountryCode } from '@elo-ui/components/icons/product-types/types'
type Attributes = {
  paymentForm: string
  src: string
  className?: string
  disabled?: boolean
  style: {
    height?: string | number
    maxHeight?: string | number
    marginRight?: string
    opacity?: number
  }
}

export const getPubKey = (
  paymentForm: string,
  providers: Record<string, string>
): 'elopageConnectPubKey' | 'stripePubKey' | '' => {
  const provider = providers[`${snakeCaseToCamelCase(paymentForm)}Provider`]

  if (provider === PAYMENT_PROVIDERS.elopageConnect) {
    return 'elopageConnectPubKey'
  }
  if (provider === PAYMENT_PROVIDERS.stripe) {
    return 'stripePubKey'
  }

  return ''
}

export const getPaymentMethodIconComponent = ({
  paymentForm,
  locale,
  variant,
  cardProvider,
  payByBankCountryCode,
}: {
  paymentForm: string
  cardProvider: string
  locale: string
  variant: LogoVariant
  payByBankCountryCode: PayByBankCountryCode
}) => {
  const AmexVisaMasterMaestro = (
    <>
      <EloAmExIcon width={20} height={20} />
      <EloVisaIcon width={70} height={20} />
      <EloMastercardIcon width={40} height={20} />
      <EloMaestroIcon width={100} height={20} />
    </>
  )

  const VisaMasterCard = (
    <>
      <EloVisaIcon width={70} height={20} />
      <EloMastercardIcon width={40} height={20} />
    </>
  )
  const isDELocale = locale?.toUpperCase() === DEFAULT_COUNTRY_CODE
  const icons = {
    card: cardProvider === PAYMENT_PROVIDERS.mangoPay ? VisaMasterCard : AmexVisaMasterMaestro,
    bank_wire: isDELocale ? <EloBankWireDeIcon width={52} height={20} /> : <EloBankWireEnIcon width={52} height={20} />,
    sepa: <EloSepaIcon width={73} height={20} />,
    paypal: <EloPaypalIcon height={20} />,
    pay_later: isDELocale ? <EloPayLaterDeIcon width={83} height={20} /> : <EloPayLaterEnIcon width={83} height={20} />,
    klarna: <EloKlarnaIcon height={20} />,
    twint: <EloTwintIcon width={47} height={20} />,
    mondu: getLocalisedMonduIcon(locale, { width: 44, height: 20 }),
    pay_by_bank: <EloPayByBankIcon height={20} variant={variant} countryCode={payByBankCountryCode} />,
    ideal: <EloIdealIcon height={20} width={48} />,
    google_pay: <EloGooglePayIcon width={49} height={20} />,
    apple_pay: <EloApplePayIcon width={49} height={20} />,
    p_24: <EloPrzelewy24Icon width={49} height={20} />,
    svea: isDELocale ? <EloPayInInstallmentsDeIcon height={15} /> : <EloPayInInstallmentsEnIcon height={15} />,
  }

  return icons[paymentForm]
}

export const getPaymentMethodIcon = (
  paymentForm: string,
  cardProvider = PAYMENT_PROVIDERS.stripe,
  newCardLogo = false
) => {
  const formByCardProvider =
    cardProvider === PAYMENT_PROVIDERS.stripe || cardProvider === PAYMENT_PROVIDERS.elopageConnect
      ? 'stripeCard'
      : paymentForm
  const iconName = paymentForm === PAYMENT_FORMS.card ? formByCardProvider : paymentForm
  const PAYMENT_METHODS_ICONS = {
    card: Card,
    stripeCard: newCardLogo ? Card : [AmEx, StripeCard],
    paypal: PayPal,
    [PAYMENT_FORMS.bankWire]: I18n.locale === LOCALES.en ? BankWire : BankWireDe,
    sepa: Sepa,
    sofort: Sofort,
    [PAYMENT_FORMS.payLater]: I18n.locale === LOCALES.en ? PayLater : PayLaterDe,
    [PAYMENT_FORMS.p24]: P24,
    ext_balance: ExtBalance,
    free: I18n.locale === 'de' ? freeDe : freeEn,
    bank_account: I18n.locale === LOCALES.en ? BankAccount : BankAccountDe,
    [PAYMENT_FORMS.applePay]: ApplePay,
    [PAYMENT_FORMS.googlePay]: GooglePay,
    [PAYMENT_FORMS.ideal]: Ideal,
    [PAYMENT_FORMS.idealSepa]: IdealSepa,
    [PAYMENT_FORMS.klarna]: Klarna,
    [PAYMENT_FORMS.twint]: Twint,
    [PAYMENT_FORMS.mondu]: getLocalisedMonduLogo(I18n.locale),
    [PAYMENT_FORMS.payByBank]: PayByBank,
    [PAYMENT_FORMS.svea]: I18n.locale === LOCALES.en ? EloSveaPayLaterEn : EloSveaPayLaterDe,
  }
  return PAYMENT_METHODS_ICONS[iconName]
}

export const getPaymentMethodTestId = (method: string): string =>
  `payment-method-${method === PAYMENT_FORMS.p24 ? P24_PAYMENT_KEY : method}`

export const createImg = (attributes: Attributes) => {
  const { paymentForm, src = '', className = '', style = {}, disabled } = attributes || {}
  const key = createId(paymentForm, src)
  const sharedProps = {
    'data-test-id': paymentForm,
    className,
    style,
    ...(disabled !== undefined ? { 'data-disabled': disabled } : {}),
  }

  if (paymentForm === PAYMENT_FORMS.payByBank) {
    return (
      <span key={key} {...sharedProps}>
        {I18n.t('shared.payment_form.instant_bank_payment')}
      </span>
    )
  }

  return <img key={key} {...sharedProps} src={src} alt={`${paymentForm} logo`} />
}
